import { useState, useMemo, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ChevronRight, ChevronDown, Plus, Search, Folder, CheckSquare, Building2, Play, CheckCircle2 } from 'lucide-react'
import { Input } from '@/shared/components/ui/input'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Label } from '@/shared/components/ui/label'
import { taskService } from '@/shared/services/taskService'
import { clientService } from '@/shared/services/clientService'
import { projectService } from '@/shared/services/projectService'
import { useWorkspaceStore } from '@/shared/stores/workspaceStore'
import { useTimerStore } from '@/shared/stores/timerStore'
import type { Project, Task, Client } from '@/shared/types/entities'

interface ProjectWithTasks extends Project {
  tasks: Task[]
  taskCount: number
}

interface ClientWithProjects extends Client {
  projects: ProjectWithTasks[]
  projectCount: number
}

interface ProjectTaskBrowserProps {
  selectedTaskId?: string | null
  onSelectTask?: (task: Task | null) => void
  onSelectProject?: (projectId: string | null) => void
  onStartTimer?: (projectId: string, taskId: string, taskName: string) => void
  showCreateButton?: boolean
  className?: string
}

export function ProjectTaskBrowser({
  selectedTaskId,
  onSelectTask,
  onSelectProject,
  onStartTimer,
  showCreateButton = true,
  className = '',
}: ProjectTaskBrowserProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedClients, setExpandedClients] = useState<Set<string>>(new Set())
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set())
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [newTaskName, setNewTaskName] = useState('')
  const [selectedProjectForTask, setSelectedProjectForTask] = useState<string>('')
  const [isCreatingTask, setIsCreatingTask] = useState(false)

  const queryClient = useQueryClient()
  const { selectedWorkspace } = useWorkspaceStore()
  const timerStore = useTimerStore()

  // Helper to format elapsed time
  const formatElapsed = (seconds: number): string => {
    const hrs = Math.floor(seconds / 3600)
    const mins = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  // Fetch clients
  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientService.getClients(),
  })

  // Fetch projects
  const { data: projects = [] } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectService.getProjects(),
  })

  // Fetch all tasks with server-side search
  const { data: allTasks = [] } = useQuery({
    queryKey: ['tasks', selectedWorkspace?.id, projects.length, searchQuery],
    queryFn: async () => {
      if (!selectedWorkspace || projects.length === 0) return []
      const tasks = await taskService.getAllTasks(searchQuery || undefined)
      return tasks
    },
    enabled: projects.length > 0 && !!selectedWorkspace,
    staleTime: 0,
  })

  // Create task mutation
  const createTaskMutation = useMutation({
    mutationFn: taskService.createTask,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      setIsCreateModalOpen(false)
      setNewTaskName('')
    },
    onError: (error: Error) => {
      console.error(`Failed to create task: ${error.message}`)
    },
  })

  // Build hierarchical data
  const clientHierarchy: ClientWithProjects[] = useMemo(() => {
    const clientsMap = new Map<string, ClientWithProjects>()

    // Initialize clients
    clients.forEach((client) => {
      clientsMap.set(client.id, {
        ...client,
        projects: [],
        projectCount: 0,
      })
    })

    // Group projects under clients
    projects.forEach((project) => {
      const clientId = project.clientId || 'uncategorized'
      
      if (!clientsMap.has(clientId) && clientId !== 'uncategorized') {
        return // Skip if client doesn't exist
      }

      const projectTasks = allTasks.filter((t) => t.projectId === project.id && !t.isArchived)
      const projectWithTasks: ProjectWithTasks = {
        ...project,
        tasks: projectTasks,
        taskCount: projectTasks.length,
      }

      if (clientId === 'uncategorized') {
        // Create a virtual "Uncategorized" client for projects without clients
        if (!clientsMap.has('uncategorized')) {
          clientsMap.set('uncategorized', {
            id: 'uncategorized',
            workspaceId: project.workspaceId,
            name: 'Uncategorized',
            isArchived: false,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            projects: [],
            projectCount: 0,
          } as ClientWithProjects)
        }
      }

      const client = clientsMap.get(clientId)
      if (client) {
        client.projects.push(projectWithTasks)
        client.projectCount = client.projects.length
      }
    })

    return Array.from(clientsMap.values()).sort((a, b) => a.name.localeCompare(b.name))
  }, [clients, projects, allTasks])

  const toggleClient = useCallback((clientId: string) => {
    setExpandedClients((prev) => {
      const next = new Set(prev)
      if (next.has(clientId)) {
        next.delete(clientId)
      } else {
        next.add(clientId)
      }
      return next
    })
  }, [])

  const toggleProject = useCallback((projectId: string) => {
    setExpandedProjects((prev) => {
      const next = new Set(prev)
      if (next.has(projectId)) {
        next.delete(projectId)
      } else {
        next.add(projectId)
      }
      return next
    })
  }, [])

  const handleTaskClick = useCallback((task: Task) => {
    onSelectTask?.(task)
  }, [onSelectTask])

  const handleStartTimer = useCallback((e: React.MouseEvent, task: Task, projectId: string) => {
    e.stopPropagation()
    onStartTimer?.(projectId, task.id, task.name)
  }, [onStartTimer])

  const handleProjectClick = useCallback((projectId: string) => {
    onSelectProject?.(projectId)
    toggleProject(projectId)
  }, [onSelectProject, toggleProject])

  const handleCreateTask = useCallback(async () => {
    if (!newTaskName.trim() || !selectedProjectForTask) return

    setIsCreatingTask(true)
    try {
      await createTaskMutation.mutateAsync({
        name: newTaskName.trim(),
        projectId: selectedProjectForTask,
      })
    } finally {
      setIsCreatingTask(false)
    }
  }, [newTaskName, selectedProjectForTask, createTaskMutation])

  const openCreateModal = useCallback((projectId?: string) => {
    setSelectedProjectForTask(projectId || projects[0]?.id || '')
    setIsCreateModalOpen(true)
  }, [projects])

  return (
    <div className={`flex flex-col h-full ${className}`}>
      {/* Search header */}
      <div className="p-3 border-b space-y-2">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search Project or Client"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8"
          />
        </div>
      </div>

      {/* Tree view */}
      <div className="flex-1 overflow-y-auto">
        {clientHierarchy.length === 0 ? (
          <div className="p-4 text-center text-muted-foreground text-sm">
            No projects found
          </div>
        ) : (
          <div className="py-2">
            {clientHierarchy.map((client) => (
              <div key={client.id} className="select-none group">
                {/* Client header */}
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleClient(client.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleClient(client.id) }}
                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-accent/50 transition-colors cursor-pointer"
                >
                  {expandedClients.has(client.id) ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <span className="flex-1 text-left font-medium text-sm">{client.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {client.projectCount} Project{client.projectCount !== 1 ? 's' : ''}
                  </span>
                  {showCreateButton && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 opacity-0 group-hover:opacity-100"
                      onClick={(e) => {
                        e.stopPropagation()
                        openCreateModal(client.projects[0]?.id)
                      }}
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                  )}
                </div>

                {/* Projects under client */}
                {expandedClients.has(client.id) && (
                  <div className="ml-4">
                    {client.projects.map((project) => (
                      <div key={project.id}>
                        {/* Project header */}
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => handleProjectClick(project.id)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleProjectClick(project.id) }}
                          className={`w-full flex items-center gap-2 px-3 py-1.5 hover:bg-accent/50 transition-colors cursor-pointer ${
                            selectedTaskId && project.tasks.some(t => t.id === selectedTaskId)
                              ? 'bg-accent/30'
                              : ''
                          }`}
                        >
                          {expandedProjects.has(project.id) ? (
                            <ChevronDown className="h-3 w-3 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="h-3 w-3 text-muted-foreground" />
                          )}
                          <Folder className="h-3 w-3 text-muted-foreground" />
                          <span className="flex-1 text-left text-sm truncate">{project.name}</span>
                          <Badge variant="secondary" className="text-xs">
                            {project.taskCount} Task{project.taskCount !== 1 ? 's' : ''}
                          </Badge>
                          {showCreateButton && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5 opacity-0 hover:opacity-100"
                              onClick={(e) => {
                                e.stopPropagation()
                                openCreateModal(project.id)
                              }}
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          )}
                        </div>

                        {/* Tasks under project */}
                        {expandedProjects.has(project.id) && (
                          <div className="ml-4">
                            {project.tasks.length === 0 ? (
                              <div className="px-3 py-1 text-xs text-muted-foreground italic">
                                No tasks yet
                              </div>
                            ) : (
                              project.tasks.map((task) => {
                                const isTimerRunningForTask = timerStore.status === 'running' && timerStore.taskId === task.id
                                const isJiraDone = task.jiraStatusCategory === 'done' || task.jiraStatusName?.toLowerCase() === 'done'
                                
                                return (
                                  <div
                                    key={task.id}
                                    className={`group flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-accent/50 transition-colors ${
                                      selectedTaskId === task.id ? 'bg-primary/10 text-primary' : ''
                                    } ${isTimerRunningForTask ? 'bg-green-50 border-l-2 border-green-500' : ''} ${
                                      isJiraDone ? 'opacity-60' : ''
                                    }`}
                                  >
                                    <button
                                      onClick={() => handleTaskClick(task)}
                                      className="flex-1 flex items-center gap-2 text-left"
                                    >
                                      {isJiraDone ? (
                                        <CheckCircle2 className="h-3.5 w-3.5 text-green-600 shrink-0" />
                                      ) : (
                                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${isTimerRunningForTask ? 'bg-green-500 animate-pulse' : 'bg-primary/60'}`} />
                                      )}
                                      <CheckSquare className="h-3 w-3 text-muted-foreground shrink-0" />
                                      <span className={`truncate ${isJiraDone ? 'line-through text-muted-foreground' : ''}`}>
                                        {'jiraIssueKey' in task && task.jiraIssueKey && (
                                          <span className="text-xs text-muted-foreground mr-1.5">{task.jiraIssueKey}</span>
                                        )}
                                        {task.name}
                                      </span>
                                      {isJiraDone && (
                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700 font-medium ml-1 shrink-0">
                                          Done
                                        </span>
                                      )}
                                    </button>
                                    
                                    {/* Show running timer inline when active */}
                                    {isTimerRunningForTask && (
                                      <span className="font-mono text-sm font-semibold text-green-600">
                                        {formatElapsed(timerStore.elapsedSeconds)}
                                      </span>
                                    )}
                                    
                                    {onStartTimer && !isTimerRunningForTask && !isJiraDone && (
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                        onClick={(e) => handleStartTimer(e, task, project.id)}
                                        title="Start timer for this task"
                                      >
                                        <Play className="h-3 w-3 text-green-600" />
                                      </Button>
                                    )}
                                  </div>
                                )
                              })
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Task Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Task</DialogTitle>
            <DialogDescription>
              Add a new task to track time against.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="task-project">Project</Label>
              <select
                id="task-project"
                value={selectedProjectForTask}
                onChange={(e) => setSelectedProjectForTask(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">Select a project...</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="task-name">Task Name</Label>
              <Input
                id="task-name"
                value={newTaskName}
                onChange={(e) => setNewTaskName(e.target.value)}
                placeholder="Enter task name..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newTaskName.trim() && selectedProjectForTask) {
                    handleCreateTask()
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateTask}
              disabled={!newTaskName.trim() || !selectedProjectForTask || isCreatingTask}
            >
              {isCreatingTask ? 'Creating...' : 'Create Task'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default ProjectTaskBrowser
